/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { defineStore } from 'pinia'
import { ref, computed, type Ref } from 'vue'

// PageStyle interface for TypeScript typing
interface PageStyle {
  id?: string
  classNames?: string
  theme?: 'xw_white' | 'xw_teal' | 'xw_grey' | 'xw_yellow' | 'xw_teal_dark'
  css?: string
  pageTopSpacer?: boolean
}

export const useThemeStore = defineStore('theme', () => {
  
  const currentTheme: Ref<string> = ref('xw-white')
  
  const previousTheme: Ref<string | null> = ref(null)
  
  const isInPanel: Ref<boolean> = ref(false)
  
  const isPanelOperationInProgress: Ref<boolean> = ref(false)
  
  const currentPageTopSpacer: Ref<boolean | null> = ref(null)
  
  // Available themes
  const availableThemes: Ref<string[]> = ref([
    'xw-white',
    'xw-teal', 
    'xw-grey',
    'xw-yellow',
    'xw-teal-dark'
  ])
  

  
  // Theme CSS class computed property
  const themeClass = computed(() => {
    return `theme-${currentTheme.value}`
  })
  
  // Clean up all theme-related classes from body
  function cleanupThemeClasses(): void {
    // Remove all theme-related classes more comprehensively
    const classList = Array.from(document.body.classList)
    classList.forEach(className => {
      if (className.startsWith('theme-') || className.startsWith('previous-theme-')) {
        document.body.classList.remove(className)
      }
    })
  }

  // Set the app theme (only applies theme classes)
  function setAppTheme(themeName: string): void {
    if (availableThemes.value.includes(themeName)) {
      const oldTheme = currentTheme.value
      currentTheme.value = themeName
      
      // Clean up all existing theme classes
      cleanupThemeClasses()
      
      // Add the new theme class
      document.body.classList.add(`theme-${themeName}`)
      
      // If in panel mode, also add previous theme class
      if (isInPanel.value && previousTheme.value) {
        document.body.classList.add(`previous-theme-${previousTheme.value}`)
      }
    } else {
      console.warn(`Theme "${themeName}" is not available. Available themes:`, availableThemes.value)
    }
  }
  
  // Store the current theme as previous theme (for panel switching)
  // Only stores if we're not already in a panel (to preserve main page theme)
  function storePreviousTheme(): void {
    if (!isInPanel.value) {
      previousTheme.value = currentTheme.value
    }
  }
  
  // Set panel mode and store main page theme
  function enterPanelMode(): void {
    // Prevent rapid panel mode changes
    if (isPanelOperationInProgress.value) {
      return
    }
    
    isPanelOperationInProgress.value = true
    
    if (!isInPanel.value) {
      previousTheme.value = currentTheme.value
      isInPanel.value = true
    }
    
    // Clear operation flag after a short delay
    setTimeout(() => {
      isPanelOperationInProgress.value = false
    }, 100)
  }
  
  // Restore the previous theme (when panels close)
  function restorePreviousTheme(): void {
    // Prevent multiple rapid restoration calls
    if (isPanelOperationInProgress.value) {
      return
    }
    
    isPanelOperationInProgress.value = true
    
    if (previousTheme.value) {
      setAppTheme(previousTheme.value)
      previousTheme.value = null
    }
    
    isInPanel.value = false
    
    // Clean up any remaining previous theme classes
    cleanupThemeClasses()
    
    // Clear flag immediately after operation completes
    isPanelOperationInProgress.value = false
  }
  
  // Set theme for main pages (non-panel pages)
  function setMainPageTheme(themeName: string): void {
    if (!isInPanel.value) {
      setAppTheme(themeName)
    }
  }
  
  // Get current theme
  function getCurrentTheme(): string {
    return currentTheme.value
  }
  
  // Check if theme is available
  function isThemeAvailable(themeName: string): boolean {
    return availableThemes.value.includes(themeName)
  }
  
  // Set page top spacer from backend page data
  function setPageTopSpacer(pageTopSpacer: boolean | undefined | null): void {
    currentPageTopSpacer.value = pageTopSpacer ?? null
  }
  
  return {
    currentTheme,
    previousTheme,
    availableThemes,
    themeClass,
    isInPanel,
    isPanelOperationInProgress,
    currentPageTopSpacer,
    setAppTheme,
    setMainPageTheme,
    storePreviousTheme,
    enterPanelMode,
    restorePreviousTheme,
    getCurrentTheme,
    isThemeAvailable,
    setPageTopSpacer
  }
})

// Export the PageStyle type for use in other files
export type { PageStyle } 